import { useMemo, useState } from "react";
import { QueryBuilder, RuleGroupType } from "react-querybuilder";
import SpecificCompaniesDisplay from "../../CuratedSearchPrompt/CuratedSearchPromptEdit/SpecificCompaniesDisplay";
import classNames from "classnames";
import { OpensearchCuratedSearch } from "../../../../__generated__/graphql";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import BulkCompanyEntryModal from "../BulkCompanyEntryModal";
import useCompanyNameHandling from "../../../../reactHooks/useCompanyNameHandling";
import PowersearchQueryRule from "./PowersearchQueryComponents/PowersearchQueryRule";
import powersearchRuleValidator from "./powersearchRuleValidator";
import PowersearchRuleGroupHeaderElements from "./PowersearchQueryComponents/PowersearchRuleGroupHeaderElements";
import PowersearchRuleGroup from "./PowersearchQueryComponents/PowersearchRuleGroup";

const CuratedInternalSearchPowerUserFilters = ({
  savedFilters,
  setSavedFilters,
  disabled = false,
}: {
  savedFilters?: OpensearchCuratedSearch | null;
  setSavedFilters?: (value: OpensearchCuratedSearch) => void;
  disabled?: boolean;
}) => {
  const isDisabled = disabled || !Boolean(setSavedFilters);

  const query = useMemo(() => {
    return JSON.parse(
      savedFilters?.opensearchFiltersBlob ?? "{}",
    ) as RuleGroupType;
  }, [savedFilters?.opensearchFiltersBlob]);

  const specificCompanies = useMemo(
    () => savedFilters?.auxiliary_filters?.companyNames || [],
    [savedFilters?.auxiliary_filters?.companyNames],
  );

  const handleRemoveCompany = (index: number) => {
    if (!setSavedFilters || !savedFilters) return;

    const updatedCompanies = [...specificCompanies];
    updatedCompanies.splice(index, 1);

    setSavedFilters({
      ...savedFilters,
      auxiliary_filters: {
        ...savedFilters.auxiliary_filters,
        companyNames: updatedCompanies,
      },
    });
  };

  const handleClearAllCompanies = () => {
    if (!setSavedFilters || !savedFilters) return;

    setSavedFilters({
      ...savedFilters,
      auxiliary_filters: {
        ...savedFilters.auxiliary_filters,
        companyNames: [],
      },
    });
  };

  const [isBulkEntryModalOpen, setIsBulkEntryModalOpen] = useState(false);

  const { companiesText, setCompaniesText, parseCompanies } =
    useCompanyNameHandling({
      initialCompanies: specificCompanies,
      onChange: (companies) => {
        if (!setSavedFilters || !savedFilters) return;

        setSavedFilters({
          ...savedFilters,
          auxiliary_filters: {
            ...savedFilters.auxiliary_filters,
            companyNames: companies,
          },
        });
      },
    });

  const handleSaveBulkCompanyEntry = () => {
    const newCompanies = parseCompanies(companiesText);

    if (!setSavedFilters || !savedFilters) return;

    setSavedFilters({
      ...savedFilters,
      auxiliary_filters: {
        ...savedFilters.auxiliary_filters,
        companyNames: newCompanies,
      },
    });
    setIsBulkEntryModalOpen(false);
  };

  return (
    <div>
      <div className={classNames("w-full")}>
        {specificCompanies.length > 0 && (
          <div className={classNames("w-full")}>
            <SpecificCompaniesDisplay
              isDisabled={isDisabled}
              specificCompanies={specificCompanies}
              onRemoveCompany={handleRemoveCompany}
              onClearAll={isDisabled ? undefined : handleClearAllCompanies}
              onEdit={
                isDisabled ? undefined : () => setIsBulkEntryModalOpen(true)
              }
              alwaysShow={true}
            />
          </div>
        )}

        {!isDisabled && specificCompanies.length === 0 && (
          <div
            className={classNames(
              "pt-3",
              "w-full",
              "flex",
              "justify-end",
              "pb-2",
            )}
          >
            <CiroButton
              analyticsField="Add specific companies in curated search"
              style={CiroButtonStyleEnum.UNSTYLED}
              onClick={() => {
                setIsBulkEntryModalOpen(true);
              }}
            >
              <div
                className={classNames(
                  "text-sm",
                  "text-orange-500",
                  "hover:text-orange-600",
                  "cursor-pointer",
                )}
              >
                Add specific companies
              </div>
            </CiroButton>
          </div>
        )}

        <BulkCompanyEntryModal
          isOpen={isBulkEntryModalOpen}
          onClose={() => setIsBulkEntryModalOpen(false)}
          onSave={handleSaveBulkCompanyEntry}
          companiesText={companiesText}
          setCompaniesText={setCompaniesText}
        />
      </div>
      <QueryBuilder
        controlClassnames={{
          queryBuilder: classNames("rounded-xl", "bg-white", "overflow-hidden"),
          header: classNames(
            isDisabled ? "hidden" : "bg-gray-100",
            isDisabled ? "" : "rounded-t",
            isDisabled ? "" : "px-4",
            isDisabled ? "" : "py-3",
            isDisabled ? "" : "border-b",
            isDisabled ? "" : "border-gray-200",
            isDisabled ? "" : "flex",
            isDisabled ? "" : "items-center",
            isDisabled ? "" : "gap-3",
          ),
          body: classNames("bg-white", "divide-y", "divide-gray-100"),
          ruleGroup: classNames(
            "bg-gray-50/50",
            "border",
            "border-gray-200",
            "rounded-lg",
            "p-3",
            "my-2",
            "shadow-sm",
          ),
        }}
        query={query}
        onQueryChange={(query) => {
          if (!setSavedFilters || !savedFilters) return;

          setSavedFilters({
            ...savedFilters,
            opensearchFiltersBlob: JSON.stringify(query),
          });
        }}
        validator={powersearchRuleValidator}
        disabled={isDisabled}
        showCombinatorsBetweenRules={true}
        controlElements={{
          rule: PowersearchQueryRule,
          ruleGroupHeaderElements: PowersearchRuleGroupHeaderElements,
          ruleGroup: PowersearchRuleGroup,
        }}
      />
    </div>
  );
};

export default CuratedInternalSearchPowerUserFilters;
